import {
  buildCollectorPayload,
  helpKey,
  isFieldRequired,
  labelKey,
  validateCollectorSection,
  visibleFields,
  type AddItemStep,
  type CollectorCategory,
  type CollectorValues,
  type CreatedItem,
  type FileCounts,
} from "@kovernow/flows/add_item";
import { acceptFor, COLLECTOR_UPLOAD_KINDS, type CollectorUploadKind, type UploadQueue } from "@kovernow/flows/uploads";
import { Button } from "@kovernow/react-ui/components/button";
import { UploadField } from "@kovernow/react-ui/components/upload-field";
import { useState, type RefObject } from "react";
import { GatedButton } from "../../components/GatedButton";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { AdvisoryNotices } from "./CollectorSectionStep";
import { ReattachList } from "./PhotosStep";
import { STEP_HEADING_ID, StepHeading, type ServerFieldErrors } from "./shared";
import { BusyLine, PipelineStatus, useUploadPipeline } from "./upload-pipeline";

/**
 * A collector's documents section: one UploadField per visible document
 * kind (PDF allowed except for item images and the selfie), with the app's
 * required and conditional rules. Shares the photos step's upload → POST
 * pipeline; the previous-losses notice sits above the submit button.
 */
export function CollectorDocumentsStep({
  category,
  values,
  today,
  back,
  headingRef,
  serverErrors,
  clearServerError,
  queue,
  ownerId,
  reattach,
  inFlight,
  onFieldError,
  onCreated,
}: {
  category: CollectorCategory;
  values: CollectorValues;
  today: string;
  back: () => void;
  headingRef: RefObject<HTMLHeadingElement | null>;
  serverErrors: ServerFieldErrors;
  clearServerError: (field: string) => void;
  queue: UploadQueue;
  ownerId: string | null;
  reattach: string[];
  inFlight: { current: boolean };
  onFieldError: (step: AddItemStep, field: string, messageKey: string) => void;
  onCreated: (created: CreatedItem) => void;
}) {
  const t = useT();
  const { addItemApi, config } = useServices();
  const [attempted, setAttempted] = useState(false);

  const fields = visibleFields(category, "documents", values);
  const visibleKinds = new Set(fields.map((f) => f.key));
  const pipeline = useUploadPipeline({
    queue,
    ownerId,
    inFlight,
    createItem: async () => {
      const payload = buildCollectorPayload(category, values, { currency: config.currency, documents: queue.documents() });
      const created = await addItemApi.create(payload);
      onCreated(created);
    },
    onFieldError: (target, messageKey) => onFieldError(target.step, target.field, messageKey),
  });
  const { qs, busy, locked, rowsFor, limitError, common } = pipeline;

  const files: FileCounts = Object.fromEntries(COLLECTOR_UPLOAD_KINDS.map((k) => [k, queue.count(k)]));
  const errors = validateCollectorSection(category, "documents", values, { today, currency: config.currency, files });

  const submit = () => {
    setAttempted(true);
    if (Object.keys(errors).length || queue.preparing()) return;
    // Files of a kind hidden since they were chosen (e.g. a storage statement
    // after switching to Home) are neither uploaded nor sent.
    for (const f of queue.getState().files) {
      if (!visibleKinds.has(f.kind)) queue.remove(f.id);
    }
    void pipeline.run(() => queue.uploadPending(ownerId));
  };

  const chosen = new Set(qs.files.map((f) => f.name));
  const toReattach = reattach.filter((n) => !chosen.has(n));

  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-6" data-testid="step-documents">
      <StepHeading headingRef={headingRef}>{t("addItem.steps.documents")}</StepHeading>

      <ReattachList names={toReattach} />

      {fields.map((field) => {
        const kind = field.key as CollectorUploadKind;
        const required = isFieldRequired(field, values, files);
        const label = t(labelKey(category, kind));
        const server = serverErrors[kind];
        const error =
          (server ? t(server) : undefined) ?? limitError(kind) ?? (attempted && errors[kind] ? t(errors[kind]!) : undefined);
        return (
          <UploadField
            key={kind}
            {...common}
            capture={kind === "selfie_with_item" ? "user" : "environment"}
            testId={`upload-${kind}`}
            label={required ? label : t("collector.optionalLabel", { label })}
            limits={t(`uploads.limits.${kind}`)}
            accept={acceptFor(kind)}
            rows={rowsFor(kind)}
            error={error}
            onFiles={(picked) => {
              clearServerError(kind);
              void queue.add(kind, picked);
            }}
          >
            {field.help ? <p className="text-sm text-muted-foreground">{t(helpKey(category, kind))}</p> : null}
          </UploadField>
        );
      })}

      <PipelineStatus uploadsFailed={pipeline.uploadsFailed} recovery={pipeline.recovery} onRetry={submit} />
      <AdvisoryNotices category={category} values={values} placement="submit" today={today} />

      <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-start">
        <div className="sm:min-w-56">
          <GatedButton
            variant="cta"
            label={t("addItem.submit")}
            busy={locked}
            unavailableReason={qs.files.some((f) => f.status === "preparing") ? t("common.loading") : undefined}
            onClick={submit}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="touch"
          aria-disabled={locked ? true : undefined}
          onClick={() => {
            if (!locked) back();
          }}
        >
          {t("addItem.back")}
        </Button>
      </div>
      <BusyLine busy={busy} />
    </section>
  );
}
